import React from 'react';
import PropTypes from 'prop-types';
import { LocalizationContext } from '../../../hoc/Localization';
import { UserContext } from '../../../hoc/User';
import { SettingsContext } from '../../../hoc/Settings';
import { SecurityContext } from '../../../hoc/Security';
import SchoolLink from '../../TinyComponents/SchoolLink';
import IconText from '../../TinyComponents/IconText';
import Rating from '../../Rating';
import usePriceFormat from '../../../utils/priceFormat';
import ScheduleMoment from '../ScheduleMoment';
import Picture from '../../Picture';
import LinkWrapper from '../../Card/LinkWrapper';
import { toggleFavourite } from '../../../utils/directory';
import ShareAndFavourite from '../../TinyComponents/ShareAndFavourite';
import Badges from '../Badges';
import Price from '../Price';
import styles from './styles.less';

const Header = ({ course, showMonthlyPrice }) => {
  const { t, locale, translate } = React.useContext(LocalizationContext);
  const { user, refreshUser } = React.useContext(UserContext);
  const {
    domainSettings: { domain },
  } = React.useContext(SettingsContext);
  const { favourite: favouriteClicked, setFavourite, handleClicked } = React.useContext(
    SecurityContext,
  );

  const [isFavourite, setIsFavourite] = React.useState(false);
  const [favourites, setFavourites] = React.useState(course.favouriteNumber);
  const [loadingFavourite, setLoadingFavourite] = React.useState(false);

  React.useEffect(() => {
    // eslint-disable-next-line react/prop-types
    if (user && user.favourites) {
      // eslint-disable-next-line react/prop-types
      const favourite = user.favourites.find(f => f.course_id === course.shortId);
      if (favourite && !isFavourite) {
        setIsFavourite(true);
      } else {
        setIsFavourite(false);
      }
    }
  }, [user]);

  React.useEffect(() => {
    if (favouriteClicked && user) {
      if (favouriteClicked === course.shortId) {
        setFavourite('');
        // eslint-disable-next-line no-use-before-define
        handleFavouriteClick();
      }
    }
  }, [favouriteClicked, user]);

  async function handleFavouriteClick() {
    if (!user) {
      setFavourite(true);
      return;
    }
    if (user && !loadingFavourite) {
      setLoadingFavourite(true);
      await toggleFavourite(course.shortId, isFavourite, 'course');
      await refreshUser();
      if (isFavourite) {
        setFavourites(Number(favourites) - 1);
      } else {
        setFavourites((Number(favourites) || 0) + 1);
      }
      setLoadingFavourite(false);
    }
    if (!user) {
      handleClicked(course.shortId);
    }
  }

  const {
    title,
    image,
    Group,
    level,
    price,
    rating,
    school,
    period,
    period_value: periodValue,
    language,
    location,
    currency,
    featured,
    discount,
    trainingType,
    coursetypes,
    free_trial: freeTrial,
    free_course: freeCourse,
    discounted_price: discountedPrice,
    monthly_price: monthlyPrice,
    'Max Group Size': maxGroupSize,
    'Min Group Size': minGroupSize,
    last_minute_start: lastMinuteStart,
    last_minute_end: lastMinuteEnd,
    last_minute_discount: lastMinuteDiscount,
    early_bird_start: earlyBirdStart,
    early_bird_end: earlyBirdEnd,
    early_bird_discount: earlyBirdDiscount,
    discount_start: discountStart,
    discount_end: discountEnd,
    online,
  } = course;

  const symbolCurrency = {
    AMD: '֏',
    USD: '$',
    EUR: '€',
    RUB: '₽',
  };

  let courseLanguage = null;

  if (coursetypes) {
    courseLanguage = translate(coursetypes, 'title');
  }

  if (courseLanguage && language && Object.keys(language).length) {
    courseLanguage += ` (${translate(language, 'Name')})`;
  } else if (language && Object.keys(language).length) {
    courseLanguage = translate(language, 'Name');
  }

  const address = location && Object.keys(location).length ? [location.city, location.street] : [];
  if (location && location.house_number) {
    address.push(location.house_number);
  }
  address.push(location.zip_code);

  let groupSize = '';

  if (Group && maxGroupSize) {
    groupSize = `${maxGroupSize}`;

    if (minGroupSize) {
      groupSize = `${minGroupSize} - ${maxGroupSize}`;
    }
  }

  let showGroup;

  if (Group) {
    showGroup = `${t('details.edit.courses.form.group_yes')} ${groupSize}`;
  } else {
    showGroup = t('details.edit.courses.form.group_no');
  }

  let favouriteUrl = '/static/images/course/heartBlack.svg';
  if (isFavourite) {
    favouriteUrl = '/static/images/course/heartChecked.svg';
  }

  const showFeatured = () => {
    if ((freeTrial && online) || !featured) {
      return false;
    }
    return true;
  };

  return (
    <div id="courseHeader" className={`${styles.searchMod} ${styles.block}`}>
      {/* ----------Image---------- */}
      <div className={styles.imageContainer}>
        <LinkWrapper data={course} reference="search">
          <Picture src={image} alt={title} />
        </LinkWrapper>
      </div>
      {/* ----------Info---------- */}
      <div className={styles.infoContainer}>
        {/* ----------Title and Rating---------- */}
        <div className={styles.header}>
          <div className={styles.shareInfo}>
            <div className={styles.flexJustified}>
              {/* ----------Rating---------- */}
              <Badges online={online} featured={showFeatured()} freeTrial={freeTrial} />
              <ShareAndFavourite
                course={course}
                domain={domain}
                favouriteUrl={favouriteUrl}
                handleFavouriteClick={handleFavouriteClick}
                locale={locale}
                school={school}
                type="search"
              />
            </div>
          </div>
          {/* ----------Title---------- */}
          <div className={styles.title}>
            <div className={styles.title_schedule}>
              <div className={styles.titleWithSchoolLink}>
                <h1 className={styles.titleNameCourse}>
                  <LinkWrapper data={course} reference="search">
                    <span style={{ color: 'black' }}>
                      {course.title || course[`title_${locale}`]}
                    </span>
                  </LinkWrapper>
                </h1>
                {!!school && !!Object.keys(school).length && <SchoolLink school={school} />}
              </div>
              <div className={styles.searchScheduleTime}>
                <ScheduleMoment
                  className={styles.searchScheduleTime}
                  style={styles}
                  data={course}
                />
              </div>
            </div>
          </div>
        </div>
        {/* ----------Description---------- */}
        <div className={styles.descriptionRow}>
          <div className={styles.descriptionCol}>
            {/* ----------Address---------- */}
            {!!address && !!address.length && (
              <>
                <div className={styles.location}>
                  <IconText
                    className={styles.address}
                    icon="/static/images/course/location.svg"
                    text={<span className={styles.wrapOneLine}>{address.join(' ')}</span>}
                  />
                </div>
              </>
            )}
            {trainingType && (
              <div className={styles.trainingType}>
                <IconText
                  icon="/static/images/course/webinar.svg"
                  text={
                    <>
                      {!!trainingType && (
                        <div className={styles.category}>{trainingType[`title_${locale}`]}</div>
                      )}
                    </>
                  }
                />
              </div>
            )}
            {!!Group && <IconText icon="/static/images/course/group.svg" text={showGroup} />}
            {!!level && (
              <IconText
                className={styles.level}
                icon="/static/images/course/level.svg"
                text={translate(level, 'Name')}
              />
            )}
          </div>
          {/* ----------Price and Stars---------- */}

          <div className={styles.footerCardInfo}>
            <Rating rating={rating} favourites={favourites || '0'} />
          </div>
          <div className={styles.priceCol}>
            <Price course={course} showMonthlyPrice={showMonthlyPrice} isSearchMode />
          </div>
        </div>
      </div>
    </div>
  );
};

Header.propTypes = {
  course: PropTypes.object,
  showMonthlyPrice: PropTypes.bool,
};

Header.defaultProps = {
  course: {},
  showMonthlyPrice: false,
};

export default Header;
